import { getAllQuestionnaireAnswers } from "@medplum/core";
import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";

import { Group, Paper, Stack, Title } from "@mantine/core";

import TextWrapper from "@/components/TextWrapper/TextWrapper";
import WordsDiff from "@/components/WordsDiff/WordsDiff";
import EncounterServiceDateDiffFields from "@/encounters/components/EncounterServiceDateDiffFields/EncounterServiceDateDiffFields";
import { GynecologicalEncounterQuestionsLinkIds } from "@/encounters/constants/gynecological-encounter";
import { resolveEncounterInfoFields } from "@/encounters/utils/encounter-detail-version-diff";
import { getEncounterServiceDateDiffContext } from "@/encounters/utils/encounter-service-date-diff";

const GynecologicalEncounterDiff = ({
  currentVersion,
  previousVersion,
  currentEncounter,
  previousEncounter,
}: {
  currentVersion?: QuestionnaireResponse;
  previousVersion?: QuestionnaireResponse;
  currentEncounter?: Encounter;
  previousEncounter?: Encounter;
}) => {
  const hasEncounterPair = Boolean(currentEncounter && previousEncounter);
  if (!hasEncounterPair && (!currentVersion || !previousVersion)) return null;

  const currentAnswers = currentVersion ? getAllQuestionnaireAnswers(currentVersion) : {};
  const previousAnswers = previousVersion ? getAllQuestionnaireAnswers(previousVersion) : {};

  const fields = resolveEncounterInfoFields(currentEncounter, previousEncounter, () => ({
    currentTemplate:
      currentAnswers[GynecologicalEncounterQuestionsLinkIds.EncounterTemplate]?.[0]?.valueCoding
        ?.display,
    previousTemplate:
      previousAnswers[GynecologicalEncounterQuestionsLinkIds.EncounterTemplate]?.[0]?.valueCoding
        ?.display,
    currentType:
      currentAnswers[GynecologicalEncounterQuestionsLinkIds.EncounterType]?.[0]?.valueCoding
        ?.display,
    previousType:
      previousAnswers[GynecologicalEncounterQuestionsLinkIds.EncounterType]?.[0]?.valueCoding
        ?.display,
    currentLocation:
      currentAnswers[GynecologicalEncounterQuestionsLinkIds.EncounterLocation]?.[0]?.valueCoding
        ?.display,
    previousLocation:
      previousAnswers[GynecologicalEncounterQuestionsLinkIds.EncounterLocation]?.[0]?.valueCoding
        ?.display,
    serviceDateDiff: getEncounterServiceDateDiffContext({
      currentAnswers,
      previousAnswers,
    }),
  }));

  const {
    currentTemplate,
    previousTemplate,
    currentType,
    previousType,
    currentLocation,
    previousLocation,
    serviceDateDiff,
  } = fields;

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Encounter Information</Title>
      </Stack>
      <Group grow align="flex-start">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="0">
              <TextWrapper fw={600}>Template</TextWrapper>
              <WordsDiff showRemoved current={currentTemplate} previous={previousTemplate} />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>Type</TextWrapper>
              <WordsDiff showRemoved current={currentType} previous={previousType} />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>Location</TextWrapper>
              <WordsDiff showRemoved current={currentLocation} previous={previousLocation} />
            </Stack>
            {serviceDateDiff && (
              <EncounterServiceDateDiffFields variant="previous" diff={serviceDateDiff} />
            )}
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="0">
              <TextWrapper fw={600}>Template</TextWrapper>
              <WordsDiff current={currentTemplate} previous={previousTemplate} />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>Type</TextWrapper>
              <WordsDiff current={currentType} previous={previousType} />
            </Stack>

            <Stack gap="0">
              <TextWrapper fw={600}>Location</TextWrapper>
              <WordsDiff current={currentLocation} previous={previousLocation} />
            </Stack>
            {serviceDateDiff && (
              <EncounterServiceDateDiffFields variant="amended" diff={serviceDateDiff} />
            )}
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default GynecologicalEncounterDiff;
